import React, { createContext, useContext, useState } from "react";
import {
  Integration,
  IntegrationProtocol,
  IntegrationSortField,
  IntegrationSortValues,
  SortingOrder,
} from "../../../api";
import {
  useCreateIntegration,
  useCreateIntegrationTemplate,
  useDeleteIntegration,
  useIntegration,
  useIntegrations,
  useIntegrationTemplate,
  useUpdateIntegration,
} from "../../../api/api.integrations";
import { useRefreshAll } from "../../../util";

export interface ActiveCell {
  row: number;
  col: keyof Integration;
  data: Integration;
}
interface ApiState {
  loading: boolean;
  error: boolean;
  disabled?: boolean;
}

type IntegrationStateContextValue = {
  activeCell: ActiveCell | null;
  integrationSortValues: IntegrationSortValues;
  integration: ReturnType<typeof useIntegration>;
  integrations: ReturnType<typeof useIntegrations>;
  integrationTemplate: ReturnType<typeof useIntegrationTemplate>;
  integrationState: ApiState;
  integrationsState: ApiState;
  integrationTemplateState: ApiState;
  createIntegrationState: ApiState;
  deleteIntegrationState: ApiState;
  updateIntegrationState: ApiState;
  setActiveCell: React.Dispatch<React.SetStateAction<ActiveCell | null>>;
  setIntegrationSortValues: React.Dispatch<React.SetStateAction<IntegrationSortValues>>;
  deleteIntegration: ReturnType<typeof useDeleteIntegration>;
  createIntegration: ReturnType<typeof useCreateIntegration>;
  updateIntegration: ReturnType<typeof useUpdateIntegration>;
  createIntegrationTemplate: ReturnType<typeof useCreateIntegrationTemplate>;
  setCreateIntegrationState: React.Dispatch<React.SetStateAction<ApiState>>;
  setDeleteIntegrationState: React.Dispatch<React.SetStateAction<ApiState>>;
  setUpdateIntegrationState: React.Dispatch<React.SetStateAction<ApiState>>;
  refreshIntegrations: () => void;
  refreshIntegration: () => void;
  refreshIntegrationTemplate: () => void;
};

const IntegrationStateContext = createContext<
  IntegrationStateContextValue | undefined
>(undefined);

/*
  This file creates a context for centralized state management of integration functionality within the integration panel.
    - Active cell tracking for UI interactions
    - Integration CRUD operations (create, read, update, delete)
    - Integration template management
    - API state tracking (loading, error, disabled states)
    - Refresh functionality for data synchronization
*/
export const IntegrationStateProvider: React.FC<{
  children: React.ReactNode;
  consumerId: string;
}> = ({ children, consumerId }) => {
  const [activeCell, setActiveCell] = useState<ActiveCell | null>(null);
  const [integrationSortValues, setIntegrationSortValues] = useState<IntegrationSortValues>({
    sort: IntegrationSortField.DateCreated,
    order: SortingOrder.Ascending,
  });
  const [createIntegrationState, setCreateIntegrationState] =
    useState<ApiState>({ loading: false, error: false });
  const [deleteIntegrationState, setDeleteIntegrationState] =
    useState<ApiState>({ loading: false, error: false });
  const [updateIntegrationState, setUpdateIntegrationState] =
    useState<ApiState>({ loading: false, error: false, disabled: true });
  const integration = useIntegration(
    activeCell?.data.id || "",
    !activeCell?.data.id
  );

  const integrations = useIntegrations(consumerId, integrationSortValues);
  const integrationTemplate = useIntegrationTemplate(
    activeCell?.data.id || "",
    activeCell?.data.protocol === IntegrationProtocol.EMAIL ||
      !activeCell?.data.id //Abort fetch if selected integration is email or no integration selected
  );
  const deleteIntegration = useDeleteIntegration(activeCell?.data.id || "");
  const createIntegration = useCreateIntegration(consumerId);
  const updateIntegration = useUpdateIntegration(activeCell?.data.id || "");
  const createIntegrationTemplate = useCreateIntegrationTemplate(
    activeCell?.data.id || ""
  );
  const refreshIntegration = useRefreshAll(integration);
  const refreshIntegrationTemplate = useRefreshAll(integrationTemplate);
  const refreshIntegrations = useRefreshAll(integrations);

  return (
    <IntegrationStateContext.Provider
      value={{
        activeCell,
        integrationSortValues,
        integration,
        integrations,
        integrationTemplate,
        integrationState: {
          loading: integration.loading,
          error: integration.failed,
        },
        integrationsState: {
          loading: integrations.loading,
          error: integrations.failed,
        },
        integrationTemplateState: {
          loading: integrationTemplate.loading,
          error: integrationTemplate.failed,
        },
        createIntegrationState: createIntegrationState,
        deleteIntegrationState: deleteIntegrationState,
        updateIntegrationState: updateIntegrationState,
        setActiveCell,
        setIntegrationSortValues,
        deleteIntegration,
        createIntegration,
        updateIntegration,
        createIntegrationTemplate,
        setCreateIntegrationState,
        setDeleteIntegrationState,
        setUpdateIntegrationState,
        refreshIntegration,
        refreshIntegrations,
        refreshIntegrationTemplate,
      }}
    >
      {children}
    </IntegrationStateContext.Provider>
  );
};

export function useIntegrationStateContext() {
  const ctx = useContext(IntegrationStateContext);
  if (!ctx)
    throw new Error("IntegrationStateContext must be used inside ApiProvider");
  return ctx;
}
